import { useLocation } from "react-router";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { scrollToTop } from "../../utilities/scrolltoTop";

// Visually-hidden but screen-reader-accessible (for the route announcer).
const srOnlyStyle = {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0,
};

const ScrollOnMount = ({ children }) => {
    // Hooks
    const { pathname, hash } = useLocation();

    // Skip focus/announce on the very first render so we don't steal focus on
    // initial page load — only manage focus on subsequent SPA navigations.
    const isFirstRender = useRef(true);
    const [announcement, setAnnouncement] = useState("");

    // Html
    const html = document.querySelector("html");

    // Jump to top
    useLayoutEffect(() => {
        // Change scroll to jump
        html.style.scrollBehavior = "auto";

        // Home page exempt
        if (pathname !== "/" && !pathname.includes("home")) {
            scrollToTop();
        }

        // Change scroll to smooth
        setTimeout(() => {
            html.style.scrollBehavior = "smooth";
        }, 100);
    }, [html.style, pathname]);

    // Move focus to the main content + announce the new page on route change.
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const main = document.getElementById("main-content");
        if (main) {
            main.focus({ preventScroll: true });
        }

        // <title> is updated asynchronously by Helmet, so read it on the next tick.
        const timeout = setTimeout(() => {
            setAnnouncement(document.title ? `${document.title}` : "Page changed");
        }, 200);

        return () => clearTimeout(timeout);
    }, [pathname]);

    // Scroll to anchor
    useEffect(() => {
        const element = document.getElementById(hash.replace("#", ""));
        window.scrollTo({
            top: element ? element.offsetTop : 0,
        });
    }, [hash]);

    return (
        <>
            <div aria-live="polite" aria-atomic="true" role="status" style={srOnlyStyle}>
                {announcement}
            </div>
            {children}
        </>
    );
};

export default ScrollOnMount;
